import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Download, Building2, TrendingUp, Star, DollarSign } from "lucide-react";
import { cn } from "@/lib/utils";
import { exportToCSV } from "./ReportExport";

const statusColors = {
  prospect: "bg-slate-100 text-slate-700",
  initial_contact: "bg-blue-100 text-blue-700",
  evaluation: "bg-amber-100 text-amber-700",
  due_diligence: "bg-lime-100 text-zinc-900",
  negotiation: "bg-lime-100 text-zinc-900",
  closed_won: "bg-emerald-100 text-emerald-700",
  closed_lost: "bg-rose-100 text-rose-700",
};

const statusLabels = {
  prospect: "Prospekt",
  initial_contact: "Innledende Kontakt",
  evaluation: "Evaluering",
  due_diligence: "Due Diligence",
  negotiation: "Forhandling",
  closed_won: "Gjennomført",
  closed_lost: "Ikke Aktuell",
};

export default function AcquisitionReport({ candidates, activities, startDate, endDate, customerName }) {
  const periodCandidates = candidates.filter(c => {
    if (!c.created_date) return true;
    const createdDate = new Date(c.created_date);
    return createdDate >= startDate && createdDate <= endDate;
  });

  const candidateData = periodCandidates.map(candidate => {
    const candidateActivities = activities.filter(a => a.candidate_id === candidate.id);
    const periodActivities = candidateActivities.filter(a => {
      const activityDate = new Date(a.date);
      return activityDate >= startDate && activityDate <= endDate;
    });

    return {
      ...candidate,
      activityCount: periodActivities.length,
    };
  });

  const stats = {
    total: candidateData.length,
    prospect: candidateData.filter(c => c.status === "prospect").length,
    evaluation: candidateData.filter(c => c.status === "evaluation").length,
    negotiation: candidateData.filter(c => c.status === "negotiation").length,
    closedWon: candidateData.filter(c => c.status === "closed_won").length,
    closedLost: candidateData.filter(c => c.status === "closed_lost").length,
    totalValue: candidateData.reduce((sum, c) => sum + (c.discussed_price || 0), 0),
    avgScore: candidateData.filter(c => c.overall_score).length > 0
      ? candidateData.filter(c => c.overall_score).reduce((sum, c) => sum + c.overall_score, 0) / candidateData.filter(c => c.overall_score).length
      : 0,
  };

  const handleExport = () => {
    const exportData = candidateData.map(c => ({
      "Bedriftsnavn": c.company_name,
      "Kontaktperson": c.contact_person,
      "E-post": c.contact_email || "",
      "Telefon": c.contact_phone || "",
      "Status": statusLabels[c.status],
      "Bransje": c.industry || "",
      "Omsetning (kr)": c.revenue || 0,
      "EBITDA (kr)": c.ebitda || 0,
      "Diskutert pris (kr)": c.discussed_price || 0,
      "Multipel": c.multiple || "",
      "Ansatte": c.employees || 0,
      "Score": c.overall_score || "",
      "Aktiviteter i periode": c.activityCount,
      "Ansvarlig": c.responsible_person_name || c.responsible_person || "",
    }));
    const namePart = customerName ? `_${customerName.replace(/\s+/g, "_")}` : "";
    exportToCSV(exportData, `oppkjopskandidater${namePart}_${new Date().toISOString().split('T')[0]}`);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Oppkjøpskandidatstatus</h3>
          <p className="text-sm text-slate-500">{customerName ? `Kunde: ${customerName}` : "Alle kunder"}</p>
        </div>
        <Button onClick={handleExport} variant="outline" size="sm">
          <Download className="w-4 h-4 mr-2" />
          Eksporter til CSV
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4 bg-white border-0 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <Building2 className="w-4 h-4 text-zinc-900" />
            <p className="text-xs text-slate-500">Totalt kandidater</p>
          </div>
          <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
        </Card>

        <Card className="p-4 bg-white border-0 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-amber-600" />
            <p className="text-xs text-slate-500">I evaluering</p>
          </div>
          <p className="text-2xl font-bold text-amber-600">{stats.evaluation}</p>
        </Card>

        <Card className="p-4 bg-white border-0 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-4 h-4 text-zinc-900" />
            <p className="text-xs text-slate-500">Forhandling</p>
          </div>
          <p className="text-2xl font-bold text-zinc-900">{stats.negotiation}</p>
        </Card>

        <Card className="p-4 bg-white border-0 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <Star className="w-4 h-4 text-emerald-600" />
            <p className="text-xs text-slate-500">Gjennomført</p>
          </div>
          <p className="text-2xl font-bold text-emerald-600">{stats.closedWon}</p>
        </Card>

        <Card className="p-4 bg-white border-0 shadow-sm col-span-2">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-4 h-4 text-zinc-900" />
            <p className="text-xs text-slate-500">Total diskutert verdi</p>
          </div>
          <p className="text-xl font-bold text-zinc-900">
            {(stats.totalValue / 1000000).toFixed(1)} MNOK
          </p>
        </Card>

        <Card className="p-4 bg-white border-0 shadow-sm col-span-2">
          <div className="flex items-center gap-2 mb-2">
            <Star className="w-4 h-4 text-amber-600" />
            <p className="text-xs text-slate-500">Gjennomsnittlig score</p>
          </div>
          <p className="text-xl font-bold text-amber-600">
            {stats.avgScore.toFixed(1)}/100
          </p>
        </Card>
      </div>

      <Card className="bg-white border-0 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600">Bedrift</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600">Bransje</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-600">Pris</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-600">EBITDA</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-600">Multipel</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-600">Score</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-600">Aktiviteter</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {candidateData.map((candidate, idx) => (
                <tr key={idx} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div>
                      <p className="text-sm font-medium text-slate-900">{candidate.company_name}</p>
                      <p className="text-xs text-slate-500">{candidate.contact_person}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={cn("border-0 text-xs", statusColors[candidate.status])}>
                      {statusLabels[candidate.status]}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{candidate.industry || "-"}</td>
                  <td className="px-4 py-3 text-sm text-slate-900 text-right">
                    {candidate.discussed_price ? `${(candidate.discussed_price / 1000000).toFixed(1)}M` : "-"}
                  </td>
                  <td className="px-4 py-3 text-sm text-emerald-600 text-right">
                    {candidate.ebitda ? `${(candidate.ebitda / 1000000).toFixed(1)}M` : "-"}
                  </td>
                  <td className="px-4 py-3 text-sm text-zinc-900 text-right">
                    {candidate.multiple ? `${candidate.multiple}x` : "-"}
                  </td>
                  <td className="px-4 py-3 text-sm text-right">
                    {candidate.overall_score ? (
                      <span className={cn("font-medium", 
                        candidate.overall_score >= 80 ? "text-emerald-600" : 
                        candidate.overall_score >= 60 ? "text-amber-600" : "text-rose-600"
                      )}>
                        {candidate.overall_score}
                      </span>
                    ) : "-"}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600 text-right">{candidate.activityCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}